import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

// Environment configuration
const ENABLED_ENVIRONMENTS = ['staging', 'production'];
// Use VITE_DATADOG_ENV from .env file (Vite exposes env vars via import.meta.env)
const currentEnv = import.meta.env.VITE_ENVIRONMENT ?? "development";

export const isDatadogEnabled = ENABLED_ENVIRONMENTS.includes(currentEnv!);

// Initialize Datadog RUM only in staging/production
export const initDatadog = (): void => {
  if (!isDatadogEnabled) {
    console.log('[Datadog] Skipping initialization - not in staging/production environment');
    return;
  }

  const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    console.warn('[Datadog] Missing APPLICATION_ID or CLIENT_TOKEN - skipping initialization');
    return;
  }

  const site = import.meta.env.VITE_DATADOG_SITE || 'datadoghq.com';

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service: 'kb',
    env: currentEnv,
    version: import.meta.env.VITE_VERSION || '1.0.0',
    sessionSampleRate: 50,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin()],
  });

  // Start session replay recording
  datadogRum.startSessionReplayRecording();

  // Log initialization status
  console.log('[Datadog] RUM initialized successfully');

  // Send a test action to verify connectivity
  datadogRum.addAction('datadog_initialized', { timestamp: new Date().toISOString() });
};

// User context types
interface DatadogUser {
  id: string;
  email?: string;
  name?: string;
  [key: string]: string | undefined;
}

// Set user context for error correlation
export const setDatadogUser = (user: DatadogUser): void => {
  if (!isDatadogEnabled) return;

  datadogRum.setUser({
    id: user.id,
    email: user.email,
    name: user.name,
  });
};

// Clear user context on logout
export const clearDatadogUser = (): void => {
  if (!isDatadogEnabled) return;

  datadogRum.clearUser();
};

// Add custom error with context
export const addDatadogError = (error: Error, context?: Record<string, unknown>): void => {
  if (!isDatadogEnabled) return;

  datadogRum.addError(error, context);
};

// Add custom action
export const addDatadogAction = (name: string, context?: Record<string, unknown>): void => {
  if (!isDatadogEnabled) return;

  datadogRum.addAction(name, context);
};

// Export the datadogRum instance for advanced usage
export { datadogRum };
